import { useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import {
  IconChartPie,
  IconFileSpreadsheet,
  IconCheck,
  IconLoader2,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import type { Currency, UploadBatch } from "@/types";
import { cfApi, cfFetcher, cfFetch } from "../../api/apiClient";
import { formatCurrency } from "../cashflow/CashflowContext";
import { ConfirmationModal } from "../ConfirmationModal";
import { useAuth } from "../../hooks/useAuth";
import { formatAppDateTime } from "../../utils/date";

const UPLOAD_LIST_KEY = cfApi("/upload");
const AGING_LIST_KEY = cfApi("/aging");

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

interface AgingReportItem {
  id: string;
  reportDate: string;
  currency: Currency;
  current: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  days90plus: number;
}

type HistoryEntry =
  | { kind: "upload"; sortAt: string; batch: UploadBatch }
  | { kind: "aging"; sortAt: string; report: AgingReportItem };

function totalAgingBalance(r: AgingReportItem): number {
  return r.current + r.days1to30 + r.days31to60 + r.days61to90 + r.days90plus;
}

export function FilesUploadHistorySection() {
  const { scopes } = useAuth();
  const canListUploads = scopes?.includes("TRANSACTIONS") ?? false;

  const { data: batches, isLoading: loadingBatches } = useSWR<UploadBatch[]>(
    canListUploads ? UPLOAD_LIST_KEY : null,
    cfFetcher,
    {
      refreshInterval: (data) =>
        asArray<UploadBatch>(data).some((b) => b.status === "processing")
          ? 3000
          : 0,
    },
  );

  const { data: agingReports, isLoading: loadingAging } = useSWR<
    AgingReportItem[]
  >(AGING_LIST_KEY, cfFetcher);

  const entries = useMemo((): HistoryEntry[] => {
    const out: HistoryEntry[] = [];
    for (const batch of asArray<UploadBatch>(batches)) {
      out.push({ kind: "upload", sortAt: batch.createdAt, batch });
    }
    for (const report of asArray<AgingReportItem>(agingReports)) {
      out.push({ kind: "aging", sortAt: report.reportDate, report });
    }
    out.sort(
      (a, b) => new Date(b.sortAt).getTime() - new Date(a.sortAt).getTime(),
    );
    return out;
  }, [batches, agingReports]);

  const hasRows = entries.length > 0;

  const revalidate = () => {
    void mutate(UPLOAD_LIST_KEY);
    void mutate(AGING_LIST_KEY);
  };

  if (!loadingBatches && !loadingAging && !hasRows) {
    return null;
  }

  const isLoading =
    (canListUploads && loadingBatches) || loadingAging;

  return (
    <section className="mt-8">
      <div className="mb-3 min-w-0">
        <h2 className="text-base font-semibold text-slate-900">
          Upload History
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Each bank import creates its own batch here until you remove it.
        </p>
      </div>
      <div className="rounded-xl mb-10 border border-slate-200 bg-white divide-y divide-slate-100">
        {(isLoading) && !hasRows ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            Loading…
          </div>
        ) : (
          entries.map((entry) =>
            entry.kind === "upload" ? (
              <UploadHistoryRow
                key={`u-${entry.batch.id}`}
                batch={entry.batch}
                onRefresh={revalidate}
              />
            ) : (
              <AgingHistoryRow
                key={`a-${entry.report.id}`}
                report={entry.report}
                onRefresh={revalidate}
              />
            ),
          )
        )}
      </div>
    </section>
  );
}

function UploadHistoryRow({
  batch,
  onRefresh,
}: {
  batch: UploadBatch;
  onRefresh: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleConfirmDelete() {
    setDeleting(true);
    try {
      const res = await cfFetch(`/upload/${batch.id}`, { method: "DELETE" });
      if (res.ok) {
        onRefresh();
      }
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  }

  const status =
    batch.status === "complete"
      ? {
          label: "Completed",
          className: "border-emerald-200 bg-emerald-50 text-emerald-700",
        }
      : batch.status === "processing"
        ? {
            label: "Processing",
            className: "border-blue-200 bg-blue-50 text-blue-700",
          }
        : {
            label: "Failed",
            className: "border-red-200 bg-red-50 text-red-700",
          };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:flex-nowrap">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
          <IconFileSpreadsheet size={22} stroke={1.75} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            Bank transactions upload
          </p>
          <p className="text-xs text-slate-500">
            {formatAppDateTime(batch.createdAt, {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })} · {batch.totalRows} transactions
            {batch.status === "processing" && batch.processedRows != null ? (
              <span className="text-blue-600">
                {" "}
                ({batch.processedRows} classified)
              </span>
            ) : null}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${status.className}`}
        >
          {batch.status === "complete" && (
            <IconCheck size={14} stroke={2.5} className="text-emerald-600" />
          )}
          {batch.status === "processing" && (
            <IconLoader2 size={14} className="animate-spin text-blue-600" />
          )}
          {batch.status === "failed" && (
            <IconX size={14} stroke={2.5} className="text-red-600" />
          )}
          {status.label}
        </span>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={deleting || batch.status === "processing"}
          aria-label="Remove upload"
          title="Remove upload"
          className="inline-flex items-center justify-center rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
        >
          {deleting ? (
            <IconLoader2 size={18} className="animate-spin text-slate-500" />
          ) : (
            <IconTrash size={18} stroke={1.75} />
          )}
        </button>
      </div>
      <ConfirmationModal
        open={confirmOpen}
        title={`Delete all ${batch.totalRows} transactions from this upload?`}
        description="This removes every transaction created by this file upload. This action cannot be undone."
        confirmLabel="Delete upload data"
        isLoading={deleting}
        onCancel={() => {
          if (!deleting) setConfirmOpen(false);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

function AgingHistoryRow({
  report,
  onRefresh,
}: {
  report: AgingReportItem;
  onRefresh: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const total = totalAgingBalance(report);

  async function handleConfirmDelete() {
    setDeleting(true);
    try {
      const res = await cfFetch(
        `/payments/aging-imports?reportDate=${encodeURIComponent(report.reportDate)}`,
        {
          method: "DELETE",
        },
      );
      if (res.ok) {
        onRefresh();
      }
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:flex-nowrap">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
          <IconChartPie size={22} stroke={1.75} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            Aging report
          </p>
          <p className="text-xs text-slate-500">
            Report date {formatAppDateTime(report.reportDate, {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })} ·{" "}
            {formatCurrency(total, report.currency)} total receivables
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
          <IconCheck size={14} stroke={2.5} className="text-emerald-600" />
          Completed
        </span>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={deleting}
          aria-label="Remove aging report"
          title="Remove aging report"
          className="inline-flex items-center justify-center rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
        >
          {deleting ? (
            <IconLoader2 size={18} className="animate-spin text-slate-500" />
          ) : (
            <IconTrash size={18} stroke={1.75} />
          )}
        </button>
      </div>
      <ConfirmationModal
        open={confirmOpen}
        title="Remove this aging report?"
        description="Dashboard and cashflow aging data will update. This action cannot be undone."
        confirmLabel="Remove aging report"
        isLoading={deleting}
        onCancel={() => {
          if (!deleting) setConfirmOpen(false);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
